import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useFormik } from 'formik'
import { enqueueSnackbar } from 'notistack'
import { ReactNode, useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { ContactInformation } from '@components/profile_form/form'
import { saveSMSConsent } from '@components/profile_form/save_sms_consent'
import { profileSchema, setupInitialValues, TProfileSchema } from '@components/profile_form/schema'
import { useLocationContext } from '@providers/location/location_provider'
import { useSessionContext } from '@providers/session/session_context'
import { buildErrorMessage } from '@shared/error/error'
import { i18WithParams as t } from '@shared/locale'
import { captureError } from '@shared/monitoring'
import { useRoutes } from 'app/use_routes'
import { ButtonActions } from 'components-ui/src/button_actions/button_actions'
import { NavBlocker } from 'components-ui/src/nav_blocker/nav_blocker'
import { Spacer } from 'components-ui/src/spacer/spacer'
import { useClientLocationUserProfile } from 'service-api/src/hooks/use_client_location_user_profiles'
import { ClientLocationUserProfileService } from 'service-api/src/services/client_location_user_profiles.service'
import { shallowEqual } from 'components-ui/src/shared/common'
import { TState } from 'service-api/src/shared/states'

const ProfileWrapper = ({ children }: { children: ReactNode }) => {
  const { navigateRoute } = useRoutes()

  const btnBackHandler = () => navigateRoute('myprofile', {})

  return (
    <Box sx={{ width: '100%' }}>
      <Button variant="text" onClick={btnBackHandler} startIcon={<KeyboardArrowLeftIcon />}>
        {t('profile.backLink')}
      </Button>
      <Spacer height={2} />
      {children}
    </Box>
  )
}

interface TMyProfileEditInnerProps {
  onFormState: (isDirty: boolean) => void
}

export const MyProfileEditInner = ({ onFormState }: TMyProfileEditInnerProps) => {
  const sessionCtx = useSessionContext()
  const { accountName: locationName = '' } = useParams()
  const lupId = sessionCtx.state.user!.lupId
  const lup = useClientLocationUserProfile(locationName, lupId)

  if (lup.isLoading) {
    return (
      <ProfileWrapper>
        <Skeleton variant="rectangular" width="100%" height={200} />
      </ProfileWrapper>
    )
  }

  return <MyProfileEditForm key={`${locationName}:${lupId}`} lup={lup} onFormState={onFormState} />
}

// Mount the draft after loading. Background refetches must not reset edits or a confirmed opt-in.
const MyProfileEditForm = ({
  onFormState,
  lup,
}: TMyProfileEditInnerProps & { lup: ReturnType<typeof useClientLocationUserProfile> }) => {
  const { navigateRoute } = useRoutes()
  const sessionCtx = useSessionContext()
  const locationCtx = useLocationContext()
  const { accountName: locationName = '' } = useParams()
  const theme = useTheme()
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))
  const lupId = sessionCtx.state.user!.lupId

  const formik = useFormik<TProfileSchema>({
    initialValues: setupInitialValues(lup.data),
    validationSchema: profileSchema,
    onSubmit: async (values): Promise<boolean> => {
      try {
        const { consentToSMS: initialConsent, ...initialContact } = formik.initialValues
        const { consentToSMS, ...contact } = values
        if (!shallowEqual(initialContact, contact)) {
          await ClientLocationUserProfileService.put(locationName, lupId, {
            displayName: `${values.firstName} ${values.lastName}`,
            firstName: values.firstName,
            lastName: values.lastName,
            phone: values.phone,
            streetAddress: values.streetAddress,
            streetAddress2: (values.streetAddress2?.trim() ?? '') === '' ? null : values.streetAddress2,
            city: values.city,
            state: values.state,
            zipCode: values.zipCode,
          })
        }
        if (consentToSMS && !initialConsent) {
          const confirmed = await saveSMSConsent(locationName, {
            lupId,
            operatorEmail: values.email,
            phoneNumber: values.phone,
          })
          // Stay on the profile form so an unconfirmed preference remains editable.
          if (!confirmed) {
            // Contact fields may already be saved; retain only the pending consent as dirty.
            formik.resetForm({ values: { ...values, consentToSMS: initialConsent } })
            await formik.setFieldValue('consentToSMS', consentToSMS, false)
            return false
          }
        }
        formik.resetForm({ values })
        await lup.refetch()
      } catch (error) {
        captureError(error as Error)
        enqueueSnackbar(buildErrorMessage(error), { variant: 'error' })
        return false
      }

      // Timeout to let form reset settle and not be catch by NavBlocker
      setTimeout(() => navigateRoute('myprofile', {}), 300)
      onFormState(false)
      enqueueSnackbar(t('profile.update.success'), { variant: 'success' })
      return true
    },
  })

  useEffect(() => onFormState(formik.dirty), [formik.dirty, onFormState])

  const onSubmitHandler = async () => {
    await formik.submitForm()
  }
  const onCancelHandler = async () => {
    if (formik.dirty) {
      formik.resetForm()
      return
    }

    setTimeout(() => navigateRoute('myprofile', {}))
  }

  const formSelectHandler = (_ev: React.SyntheticEvent, value: TState | null) => {
    formik.setFieldValue('state', value?.code ?? '')
  }

  return (
    <ProfileWrapper>
      <Paper
        elevation={1}
        sx={{
          p: 2,
          borderRadius: `${isBelowMD ? 0 : theme.shape.borderRadius}px`,
        }}
      >
        <Box>
          <Typography variant="h5">{t('profile.edit.title')}</Typography>
          <Spacer height={3} />
          <ContactInformation
            formik={formik}
            onChange={formik.handleChange}
            onChangeSelect={formSelectHandler}
            size="medium"
            smsConsent={{
              checked: formik.values.consentToSMS,
              confirmed: formik.initialValues.consentToSMS,
              isSaving: formik.isSubmitting,
              locationName: locationCtx.location?.displayName || '',
              onChange: (checked) => formik.setFieldValue('consentToSMS', checked),
            }}
          />
          <Spacer height={2} />
          <ButtonActions
            onAction={onSubmitHandler}
            actionEnabled={formik.dirty}
            disableCancelWhileSaving
            onCancel={onCancelHandler}
            invertButtons
          />
        </Box>
      </Paper>
    </ProfileWrapper>
  )
}

export const MyProfileEdit = () => {
  const [isFormDirty, setIsFormDirty] = useState(false)
  const formStateHandler = useCallback((isDirty: boolean) => setIsFormDirty(isDirty), [])

  return (
    <NavBlocker
      sx={{ px: 3, py: 2, width: 444 }}
      shouldBlock={isFormDirty}
      promptTitle={t('profile.navBlocker.title')}
      promptContent={t('profile.navBlocker.content')}
      goBackText={t('profile.navBlocker.back')}
      proceedText={t('profile.navBlocker.proceed')}
    >
      <MyProfileEditInner onFormState={formStateHandler} />
    </NavBlocker>
  )
}
